import { unpublishIndustryApi, publishIndustryApi, syncIndustryApi, updateSubjectOrderApi } from "@/api/services/admin/learning-content";
import { IconCheckCircleFill, IconDangerous, IconPlus } from "@/assets/icons";
import { CardContainer } from "@/components/commons/CardContainer";
import { ItemInfo } from "@/components/commons/ItemInfo";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Modal } from "@/components/elements/Modal";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { ACTION_LABEL, STATUS_LABEL } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CategoryInfo } from "@/features/learning-content/components/CategoryInfo";
import { ExamsTable } from "@/features/learning-content/components/ExamsTable";
import { SubjectsTable } from "@/features/learning-content/components/SubjectsTable";
import { MockExamContentSection } from "@/features/learning-content/components/MockExamContentSection";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { useExamList } from "@/features/learning-content/hooks/useExamList";
import { useSubjectList } from "@/features/learning-content/hooks/useSubjectList";
import { useUpdateIndustryMemo } from "@/features/learning-content/hooks/useUpdateIndustryMemo";
import { useVocabularyBook } from "@/features/learning-content/hooks/useVocabularyBook";
import { TIndustryDetail, TSubjectInfo } from "@/features/learning-content/types/learningContent";
import { useDialog } from "@/hooks/useDialog";
import { useDraggableTable } from "@/hooks/useDraggableTable";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { getDynamicRoute, getExternalLink } from "@/utils/url";
import { dayjs } from "@/utils/dayjs";
import FormatListNumberedOutlinedIcon from "@mui/icons-material/FormatListNumberedOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Chip, CircularProgress, Link, Stack, Typography, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

type TIndustryDetailPageProps = {};

export const IndustryDetailPage: FC<TIndustryDetailPageProps> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search, state: locationState } = useLocation();
  const isValidId = Boolean(id) && /^\d+$/.test(id!);

  useEffect(() => {
    if (!isValidId) {
      navigate(AppRoutes.pageNotFound, { replace: true });
    }
  }, [isValidId, navigate]);
  const toast = useToast();
  const scrollToSection = (locationState as { scrollToSection?: number })?.scrollToSection;
  const queryClient = useQueryClient();
  const deactivateDialog = useDialog();
  const activateDialog = useDialog();
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isSyncingIndustry, setIsSyncingIndustry] = useState(false);
  const [isPublishingIndustry, setIsPublishingIndustry] = useState(false);

  const examPage = Number(new URLSearchParams(search).get("page") || 1);
  const { data: industryData, isLoading } = useIndustryDetail(id);
  const { data: subjectsData, isLoading: isLoadingSubjects, isFetching: isFetchingSubjects } = useSubjectList(id);
  const { data: examsData, isLoading: isLoadingExams, isFetching: isFetchingExams } = useExamList(id, examPage);
  const updateMemo = useUpdateIndustryMemo(id);
  const { data: vocabularyBookData } = useVocabularyBook(id);

  const handleMemoBlur = (value: string) => {
    if (!industryData) return;
    updateMemo.mutate({ name: industryData.name, examType: industryData.examType, memo: value });
  };

  // Transform subjects API response to match TSubjectInfo type
  const subjects: TSubjectInfo[] = useMemo(() => {
    if (!subjectsData?.data) return [];
    return subjectsData.data.map((item) => ({
      id: item.id,
      isActive: item.isActive,
      partType: item.partType ?? "",
      subjectName: item.name,
      vocabularyCount: item.vocabularyCount ?? 0,
      questionCount: item.questionCount ?? 0,
      passScore: item.passingScore ?? 0,
      questionScore: item.questionScore ?? 0,
      totalScore: item.totalScore ?? 0,
      lastUpdatedDate: item.updatedAt ?? "",
    }));
  }, [subjectsData]);

  // Drag-and-drop reordering for subjects table
  const {
    rows: subjectRows,
    isSaving: isSavingOrder,
    onDragEnd: handleSubjectDragEnd,
  } = useDraggableTable({
    initialData: subjects,
    getItemId: (item: TSubjectInfo) => item.id,
    onReorder: async (orderedIds) => {
      if (!id) return;
      try {
        // No reverse needed: backend sorts subjects by displayOrder ASC (unlike industries which use DESC)
        await updateSubjectOrderApi(id, { subjectIds: orderedIds as number[] });
        toast.success(API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_SUBJECT_LIST, id] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
      } catch (error) {
        showError(error);
        throw error; // Re-throw to trigger rollback in hook
      }
    },
  });

  const hasActiveExam = useMemo(() => {
    return (examsData?.data ?? []).some((exam) => exam.status === "active");
  }, [examsData?.data]);

  // Enable publish only when: at least 1 active exam AND at least 1 active subject
  const canActivate = useMemo(() => {
    const hasActiveSubject = subjects.some((s) => s.isActive);
    return hasActiveExam && hasActiveSubject;
  }, [hasActiveExam, subjects]);

  const hasSyncedAt = Boolean(industryData?.lastSyncedAt);

  const latestExamUpdatedDate = useMemo(() => {
    const latestDate = (examsData?.data ?? [])
      .map((exam) => exam.updatedAt ?? exam.createdAt)
      .filter((date): date is string => Boolean(date))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];

    return latestDate ? dayjs(latestDate).format("YYYY/MM/DD") : "-";
  }, [examsData?.data]);

  // Transform API response to match page display format
  const data: TIndustryDetail = useMemo(() => {
    if (!industryData) {
      return {
        id: 0,
        name: "",
        status: "無公開",
        statusLabel: "無公開",
        publishedAt: "----/--/--",
        examType: "",
        publishSchedule: "-",
        examInfoLink: "-",
        lastUpdatedDate: "-",
        notes: "",
        publicStatus: "未公開",
        lastSyncedAt: "-",
        hasUnpublishedChanges: false,
        hasPendingSync: false,
        publicDate: "-",
        publicTimeAllDay: false,
        exams: [],
        subjects: subjectRows,
        vocabulary: {
          vocabularyCount: vocabularyBookData?.vocabularyCount ?? 0,
          lastUpdatedDate: vocabularyBookData?.updatedAt ?? "-",
        },
      };
    }

    return {
      id: industryData.id,
      name: industryData.name,
      status: industryData.isPublished ? STATUS_LABEL.ENABLED : "無公開",
      statusLabel: industryData.isPublished ? STATUS_LABEL.ENABLED : "無公開",
      publishedAt: industryData.publishedAt ? dayjs(industryData.publishedAt).format("YYYY/MM/DD") : "----/--/--",
      examType: industryData.examType,
      publishSchedule: industryData.examInfoReleaseEstimate ?? "-",
      examInfoLink: industryData.examInfoUrl ?? "-",
      lastUpdatedDate: latestExamUpdatedDate,
      notes: industryData.internalMemo ?? "",
      // Below fields require additional API calls or are managed separately
      publicStatus: industryData.lastSyncedAt ? "公開済み" : "未公開",
      lastSyncedAt: industryData.lastSyncedAt ? dayjs(industryData.lastSyncedAt).format("YYYY/MM/DD HH:mm:ss") : "-",
      hasUnpublishedChanges: industryData.hasUnpublishedChanges ?? false,
      hasPendingSync: industryData.hasPendingSync ?? false,
      publicDate: "-",
      publicTimeAllDay: false,
      exams: [],
      subjects: subjectRows,
      vocabulary: {
        vocabularyCount: vocabularyBookData?.vocabularyCount ?? 0,
        lastUpdatedDate: vocabularyBookData?.updatedAt ?? "-",
      },
    };
  }, [industryData, subjectRows, vocabularyBookData, latestExamUpdatedDate]);

  const handleAddExam = () => {
    if (!id) return;
    // Find the latest exam by createdAt to use as base for new entry
    const exams = examsData?.data ?? [];
    const latestExam =
      exams.length > 0
        ? exams.reduce((latest, exam) => {
            if (!latest.createdAt) return exam;
            if (!exam.createdAt) return latest;
            return new Date(exam.createdAt) > new Date(latest.createdAt) ? exam : latest;
          })
        : null;
    navigate(getDynamicRoute(AppRoutes.learningContentExamCreate, { id }), {
      state: latestExam ? { copyFromExamId: latestExam.id } : undefined,
    });
  };

  const handleAddSubject = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.learningContentSubjectCreate, { id }));
    }
  };

  const handleUnpublish = async () => {
    const confirmed = await deactivateDialog.show();
    if (!confirmed || !id) return;

    setIsDeactivating(true);
    try {
      const rs = await unpublishIndustryApi(id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_LIST] });
    } catch (error) {
      showError(error);
    } finally {
      setIsDeactivating(false);
    }
  };

  const handlePublish = async () => {
    if (!id) return;
    if (!hasSyncedAt && !canActivate) return;

    setIsSyncingIndustry(true);
    try {
      const rs = await syncIndustryApi(id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_SUBJECT_LIST, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_EXAM_LIST, id] });
    } catch (error) {
      showError(error);
    } finally {
      setIsSyncingIndustry(false);
    }
  };

  const handlePublishIndustry = async () => {
    if (!id || !hasActiveExam) return;
    const confirmed = await activateDialog.show();
    if (!confirmed) return;

    setIsPublishingIndustry(true);
    try {
      const rs = await publishIndustryApi(id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_LIST] });
    } catch (error) {
      showError(error);
    } finally {
      setIsPublishingIndustry(false);
    }
  };

  const handleEdit = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.industryEdit, { id }));
    }
  };

  const handleWordEdit = () => {
    if (!id) return;
    navigate(getDynamicRoute(AppRoutes.learningContentWordDetail, { id }));
  };

  // Show loading state while fetching
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DetailWrapper gap={3}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            {data.name}のコンテンツ管理
            <Chip label={data.status} color={data.status === STATUS_LABEL.ENABLED ? "success" : "default"} size="small" />
          </Typography>
        }
        description={
          <Typography variant="body14">
            分野情報やコンテンツを編集できます。分野の公開（提供）には最低でも1つ以上の試験情報が必要です。コンテンツを提供する場合は、最低でも1つ以上のWEEKが必要です。
          </Typography>
        }
        enablePageBack={false}
      />

      <MenuNavigate
        initialActiveIndex={scrollToSection}
        menuPrefix={
          <Box sx={{ p: "16px 16px 12px 16px" }}>
            <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
              {data.name}
            </Typography>
          </Box>
        }
        contents={[
          {
            title: "分野情報",
            content: (
              <CardContainer sx={{ borderRadius: "8px" }}>
                <CategoryInfo
                  categoryName={data.name}
                  status={data.statusLabel}
                  publishedAt={data.publishedAt}
                  notes={data.notes}
                  onMemoBlur={handleMemoBlur}
                  isSavingMemo={updateMemo.isPending}
                />
              </CardContainer>
            ),
          },
          {
            title: "試験情報",
            content: (
              <CardContainer sx={{ borderRadius: "8px" }}>
                <Stack gap={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="sub16Bold">試験情報の一覧 ({examsData?.data?.length ?? 0})</Typography>
                    <Button size="sm" variant="outline" onClick={handleAddExam} startIcon={<IconPlus />}>
                      新しい試験情報を登録
                    </Button>
                  </Stack>

                  {/* Exam info header row - 4-4-4 grid */}
                  <Stack direction="row">
                    <Stack sx={{ flex: 1 }}>
                      <ItemInfo label="試験情報の公開目安" value={data.publishSchedule} isShowDivider={false} />
                    </Stack>
                    <Stack gap={0.5} sx={{ flex: 1 }}>
                      <Typography variant="body14" sx={{ color: "text.secondary" }}>
                        試験情報リンク
                      </Typography>
                      {data.examInfoLink && data.examInfoLink !== "-" ? (
                        <Link
                          variant="body14Semi"
                          target="_blank"
                          underline="none"
                          color="primary"
                          href={getExternalLink(data.examInfoLink)}
                          rel="noopener noreferrer"
                          sx={{ "&:hover": { textDecoration: "underline" } }}
                        >
                          {industryData?.examOrganization || data.examInfoLink}
                        </Link>
                      ) : (
                        <Typography variant="body14Semi">-</Typography>
                      )}
                    </Stack>
                    <Stack sx={{ flex: 1 }}>
                      <ItemInfo label="最終更新日" value={data.lastUpdatedDate} isShowDivider={false} />
                    </Stack>
                  </Stack>

                  <ExamsTable
                    isLoading={isLoadingExams}
                    isFetching={isFetchingExams}
                    data={examsData?.data ?? []}
                    totalPage={examsData?.pagination?.totalPage ?? 1}
                    industryId={id || ""}
                  />
                </Stack>
              </CardContainer>
            ),
          },
          {
            title: "WEEK別コンテンツ",
            content: (
              <CardContainer sx={{ borderRadius: "8px" }}>
                <Stack gap={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="head20Bold">WEEK別コンテンツ ({data.subjects.length}週分)</Typography>
                    <Button size="sm" variant="outline" onClick={handleAddSubject} startIcon={<IconPlus />}>
                      新しいWEEKを登録
                    </Button>
                  </Stack>

                  <Typography variant="body14" sx={{ color: "text.secondary" }}>
                    カリキュラムは1週間の学習分を示すWEEKで構成されます。基本は難易度等を勘案しつつ科目をWEEK単位に分割してください。
                    <br />
                    なお、すべてのWEEKを完了した学習者は残りの期間で模擬試験の実施が促されます。
                  </Typography>

                  <SubjectsTable
                    isLoading={isLoadingSubjects}
                    isFetching={isFetchingSubjects || isSavingOrder}
                    data={data.subjects}
                    learningContentId={id || ""}
                    onDragEnd={handleSubjectDragEnd}
                  />

                  <Box sx={{ bgcolor: "background.orange", p: 2, borderRadius: "8px" }}>
                    <Typography variant="body14" component="p" sx={{ color: "#EC7126" }}>
                      WEEKの編集や並び替えを行った場合、下書き状態として保存されます。システムに反映するには公開を実行してください。
                    </Typography>
                    <Typography variant="body14" component="p" sx={{ color: "#EC7126", mt: 1 }}>
                      WEEK数や問題の増減をした場合、すでに受講中の方の進行状態に影響が生じます。公開するタイミングは慎重に判断してください。
                    </Typography>
                  </Box>

                  <Box sx={{ px: 3, py: 2, border: "1px solid", borderColor: "divider", borderRadius: "8px" }}>
                    <Stack direction="row" alignItems="center">
                      <Typography variant="body14" sx={{ flex: 1 }}>
                        公開ステータス：{data.publicStatus}
                      </Typography>
                      <Typography variant="body14" sx={{ flex: 1 }}>
                        公開日時：{data.lastSyncedAt}
                      </Typography>
                      <Typography variant="body14" sx={{ flex: 1 }}>
                        差分：{hasSyncedAt && data.hasUnpublishedChanges ? "あり" : "なし"}
                      </Typography>
                      <Button
                        variant="primary"
                        onClick={handlePublish}
                        disabled={isSyncingIndustry || (hasSyncedAt ? !data.hasPendingSync : !canActivate)}
                        startIcon={<SyncIcon />}
                      >
                        {ACTION_LABEL.CONTENT_PUBLISHED}
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </CardContainer>
            ),
          },
          {
            title: "単語帳コンテンツ",
            content: (
              <CardContainer sx={{ borderRadius: "8px" }}>
                <Stack gap={3}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="sub16Bold">単語帳コンテンツ</Typography>
                    <Button size="sm" variant="outline" onClick={handleWordEdit} startIcon={<FormatListNumberedOutlinedIcon sx={{ fontSize: 18 }} />}>
                      単語帳管理
                    </Button>
                  </Stack>
                  <Typography variant="body14" sx={{ color: "text.secondary" }}>
                    単語データは単語帳とWEEK別単語の2種類があります。単語帳は本分野全体を通して学習するもので、カリキュラムに組まれません。
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", px: 3, py: 2 }}
                  >
                    <Typography variant="body14">単語練習の登録語彙数：{data.vocabulary.vocabularyCount}件</Typography>
                    <Typography variant="body14">更新日：{data.vocabulary.lastUpdatedDate}</Typography>
                  </Stack>
                </Stack>
              </CardContainer>
            ),
          },
          {
            title: "模擬試験コンテンツ",
            content: <MockExamContentSection industryId={id || ""} />,
          },
        ]}
        contentAffix={
          <FooterContainer>
            <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
              <Button size="sm-round" variant="secondary" onClick={() => navigate(AppRoutes.learningContent)}>
                戻る
              </Button>
              <Stack direction="row" gap={2}>
                {industryData?.isPublished && (
                  <Button variant="danger" onClick={handleUnpublish} startIcon={<IconDangerous />} disabled={isDeactivating}>
                    {ACTION_LABEL.INDUSTRY_UNPUBLISHED}
                  </Button>
                )}
                <Button variant="outline" onClick={handleEdit}>
                  受験分野情報を編集
                </Button>
                {!industryData?.isPublished && (
                  <Button
                    variant="primary"
                    onClick={handlePublishIndustry}
                    startIcon={<IconCheckCircleFill />}
                    disabled={!hasActiveExam || isPublishingIndustry}
                  >
                    {ACTION_LABEL.INDUSTRY_PUBLISHED}
                  </Button>
                )}
              </Stack>
            </Stack>
          </FooterContainer>
        }
      />

      <ConfirmDeleteModal
        open={deactivateDialog.open}
        onClose={deactivateDialog.cancel}
        onOk={deactivateDialog.confirm}
        message="受講生が利用中のコンテンツおよび受験手続き管理機能の多くが機能を停止します。本当に無効化してもよろしいですか？"
      />
      <Modal
        isOpen={activateDialog.open}
        onClose={activateDialog.cancel}
        onOk={activateDialog.confirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
      >
        <Stack mb={2}>
          <Typography>この操作は試験情報および有効な全ての科目別コンテンツが公開されます。有効化してもよろしいですか？</Typography>
        </Stack>
      </Modal>
    </DetailWrapper>
  );
};

const DetailWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
